import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { doc, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db } from '../firebase/config';
import { updateUserProfile } from '../store/slices/authSlice';
import { usePageTitle } from '../context/PageTitleContext';
import './Profile.scss';

function Profile() {
  const { user } = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const [name, setName] = useState(user?.name || '');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [previewImage, setPreviewImage] = useState(user?.profilePicture || null);
  const fileInputRef = useRef(null);
  const { setPageTitle } = usePageTitle();
  
  // Set page title when component mounts
  useEffect(() => {
    setPageTitle("Profile Settings");
  }, [setPageTitle]);
  
  // Handle profile picture upload
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file type
    if (!file.type.match('image.*')) {
      setMessage({ type: 'error', text: 'Please select an image file.' });
      return;
    }
    
    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Image size should be less than 2MB.' });
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewImage(e.target.result);
    };
    reader.readAsDataURL(file);
  };
  
  // Upload image to Firebase Storage
  const uploadImage = async (file) => {
    if (!file) return null;
    
    const storage = getStorage();
    const storageRef = ref(storage, `profile_pictures/${user.uid}_${Date.now()}`);
    
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  };
  
  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setMessage({ type: 'error', text: 'Name cannot be empty.' });
      return;
    }
    
    setIsLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      // Reference to user document
      const userRef = doc(db, 'fredoRegisteredUsers', user.uid);
      
      // Prepare update data
      const updateData = { name };
      
      // Check if we need to upload a new profile picture
      if (fileInputRef.current?.files[0]) {
        const profilePictureURL = await uploadImage(fileInputRef.current.files[0]);
        if (profilePictureURL) {
          updateData.profilePicture = profilePictureURL;
        }
      }
      
      // Update in Firestore
      await updateDoc(userRef, updateData);
      
      // Update in Redux state
      dispatch(updateUserProfile(updateData));
      
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({ type: 'error', text: 'Failed to update profile. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Get user initials for avatar fallback
  const getUserInitials = () => {
    if (!user?.name) return 'U';
    return user.name.split(' ')
      .map(name => name[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };
  
  return (
    <div className="profile-page">
      <div className="profile-content">
        <div className="card">
          <div className="card-header">
            <h2>Personal Information</h2>
          </div>
          
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="profile-picture-section">
                <div className="current-picture">
                  {previewImage ? (
                    <img src={previewImage} alt="Profile Preview" />
                  ) : (
                    <div className="avatar-initials">{getUserInitials()}</div>
                  )}
                </div>
                
                <div className="picture-upload">
                  <input 
                    type="file" 
                    id="profile-picture" 
                    accept="image/*" 
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    className="file-input"
                  />
                  <label htmlFor="profile-picture" className="upload-button">
                    Choose Photo
                  </label>
                  <p className="upload-hint">JPEG or PNG, max 2MB</p>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="name">Full Name</label>
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your full name"
                  required
                />
              </div>
              
              <div className="form-group disabled">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  value={user?.email || ''}
                  disabled
                  placeholder="Your email address"
                />
                <p className="field-hint">Email cannot be changed</p>
              </div>
              
              {message.text && (
                <div className={`message ${message.type}`}>
                  {message.text}
                </div>
              )}
              
              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn btn-primary" 
                  disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile; 